import { create } from "zustand";
import { applyPatches, enablePatches, produceWithPatches, type Patch } from "immer";
import type { EmailDocument } from "@edm/schema";
import { ApiCallError, emailsApi, type RenderWarning } from "../../api/client";

enablePatches();

const HISTORY_LIMIT = 100;
const COALESCE_MS = 750;
const AUTOSAVE_MS = 10_000;

interface HistoryEntry {
    patches: Patch[];
    inversePatches: Patch[];
    coalesceKey?: string;
    at: number;
}

export interface ConflictState {
    currentVersion: number;
    updatedBy: string;
    serverDocument: EmailDocument;
    myDocument: EmailDocument;
}

export type SaveState = "idle" | "dirty" | "saving" | "saved" | "conflict" | "error";

/** Transient, self-clearing status message (drop feedback, limits, etc.). */
export interface Toast {
    id: number;
    message: string;
    kind: "info" | "error";
}

/** Where the next drop would land, for the between-blocks insertion line. */
export type DropHint = { nodeId: string; side: "before" | "after" } | null;

interface EditorState {
    emailId: string | null;
    document: EmailDocument | null;
    /** Version of the last successful save (optimistic concurrency base). */
    savedVersion: number;
    saveState: SaveState;
    saveError: string | null;
    conflict: ConflictState | null;
    selectedId: string | null;
    /** Bumped whenever the document changes from OUTSIDE a rich text editor
     * (undo/redo/restore) so mounted Tiptap instances re-sync. */
    externalEpoch: number;
    validation: RenderWarning[];

    undoStack: HistoryEntry[];
    redoStack: HistoryEntry[];
    /** Bumped when a saved block is created so the palette refetches. */
    savedBlocksVersion: number;
    bumpSavedBlocks(): void;

    /** Self-clearing status toast (drag-and-drop feedback, limits, errors). */
    toast: Toast | null;
    notify(message: string, kind?: "info" | "error"): void;
    dismissToast(): void;

    /** Live insertion target while dragging, for the between-blocks drop line. */
    dropHint: DropHint;
    setDropHint(hint: DropHint): void;

    load(emailId: string, document: EmailDocument, version: number): void;
    applyDocChange(
        recipe: (draft: EmailDocument) => void,
        opts?: { coalesceKey?: string; fromRichText?: boolean },
    ): void;
    undo(): void;
    redo(): void;
    canUndo(): boolean;
    canRedo(): boolean;
    select(id: string | null): void;
    markSaved(version: number): void;
    save(): Promise<void>;
    resolveConflictReload(): void;
    resolveConflictOverwrite(): Promise<void>;
    setValidation(warnings: RenderWarning[]): void;
}

export const useEditorStore = create<EditorState>((set, get) => {
    if (import.meta.env.DEV && typeof window !== "undefined") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__edmStore = { get: () => get() };
    }
    let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
    let toastTimer: ReturnType<typeof setTimeout> | null = null;
    let toastSeq = 0;

    const scheduleAutosave = () => {
        if (autosaveTimer) clearTimeout(autosaveTimer);
        autosaveTimer = setTimeout(() => {
            const s = get();
            if (s.saveState === "dirty") void doSave("autosave");
        }, AUTOSAVE_MS);
    };

    const doSave = async (kind: "manual" | "autosave") => {
        const s = get();
        if (!s.emailId || !s.document || s.saveState === "saving" || s.conflict) return;
        set({ saveState: "saving", saveError: null });
        try {
            const fn = kind === "manual" ? emailsApi.save : emailsApi.autosave;
            const result = await fn(s.emailId, s.document, s.savedVersion);
            // Document may have changed while the request was in flight.
            const now = get();
            set({
                savedVersion: result.version,
                saveState: now.document === s.document ? "saved" : "dirty",
            });
            if (get().saveState === "dirty") scheduleAutosave();
        } catch (e) {
            if (e instanceof ApiCallError && e.code === "VERSION_CONFLICT") {
                const details = e.details as {
                    currentVersion: number;
                    updatedBy: string;
                    document: EmailDocument;
                };
                set({
                    saveState: "conflict",
                    conflict: {
                        currentVersion: details.currentVersion,
                        updatedBy: details.updatedBy,
                        serverDocument: details.document,
                        myDocument: get().document!,
                    },
                });
            } else {
                set({
                    saveState: "error",
                    saveError: e instanceof Error ? e.message : String(e),
                });
                scheduleAutosave(); // retry later
            }
        }
    };

    return {
        emailId: null,
        document: null,
        savedVersion: 1,
        saveState: "idle",
        saveError: null,
        conflict: null,
        selectedId: null,
        externalEpoch: 0,
        validation: [],
        undoStack: [],
        redoStack: [],
        savedBlocksVersion: 0,

        bumpSavedBlocks() {
            set({ savedBlocksVersion: get().savedBlocksVersion + 1 });
        },

        toast: null,
        notify(message, kind = "info") {
            const id = ++toastSeq;
            set({ toast: { id, message, kind } });
            if (toastTimer) clearTimeout(toastTimer);
            toastTimer = setTimeout(() => {
                if (get().toast?.id === id) set({ toast: null });
            }, 3600);
        },
        dismissToast() {
            if (toastTimer) clearTimeout(toastTimer);
            set({ toast: null });
        },

        dropHint: null,
        setDropHint(hint) {
            const cur = get().dropHint;
            if (cur === hint) return;
            if (
                cur &&
                hint &&
                cur.nodeId === hint.nodeId &&
                cur.side === hint.side
            ) {
                return;
            }
            set({ dropHint: hint });
        },

        load(emailId, document, version) {
            if (autosaveTimer) clearTimeout(autosaveTimer);
            set({
                emailId,
                document,
                savedVersion: version,
                saveState: "idle",
                saveError: null,
                conflict: null,
                selectedId: null,
                undoStack: [],
                redoStack: [],
                validation: [],
                externalEpoch: get().externalEpoch + 1,
            });
        },

        applyDocChange(recipe, opts) {
            const s = get();
            if (!s.document) return;
            const [next, patches, inversePatches] = produceWithPatches(s.document, recipe);
            if (patches.length === 0) return;

            let undoStack = s.undoStack;
            const last = undoStack[undoStack.length - 1];
            const now = Date.now();
            if (
                opts?.coalesceKey &&
                last?.coalesceKey === opts.coalesceKey &&
                now - last.at < COALESCE_MS
            ) {
                // Merge into the previous entry: keep its inverse, extend patches.
                undoStack = [
                    ...undoStack.slice(0, -1),
                    {
                        patches: [...last.patches, ...patches],
                        inversePatches: [...inversePatches, ...last.inversePatches],
                        coalesceKey: opts.coalesceKey,
                        at: now,
                    },
                ];
            } else {
                undoStack = [
                    ...undoStack.slice(-(HISTORY_LIMIT - 1)),
                    { patches, inversePatches, coalesceKey: opts?.coalesceKey, at: now },
                ];
            }

            set({
                document: next,
                undoStack,
                redoStack: [],
                saveState: s.saveState === "saving" ? s.saveState : "dirty",
                externalEpoch: opts?.fromRichText ? s.externalEpoch : s.externalEpoch + 1,
            });
            scheduleAutosave();
        },

        undo() {
            const s = get();
            if (!s.document || s.undoStack.length === 0) return;
            const entry = s.undoStack[s.undoStack.length - 1]!;
            const document = applyPatches(s.document, entry.inversePatches);
            set({
                document,
                undoStack: s.undoStack.slice(0, -1),
                redoStack: [...s.redoStack, entry],
                saveState: "dirty",
                externalEpoch: s.externalEpoch + 1,
            });
            scheduleAutosave();
        },

        redo() {
            const s = get();
            if (!s.document || s.redoStack.length === 0) return;
            const entry = s.redoStack[s.redoStack.length - 1]!;
            const document = applyPatches(s.document, entry.patches);
            set({
                document,
                redoStack: s.redoStack.slice(0, -1),
                undoStack: [...s.undoStack, entry],
                saveState: "dirty",
                externalEpoch: s.externalEpoch + 1,
            });
            scheduleAutosave();
        },

        canUndo: () => get().undoStack.length > 0,
        canRedo: () => get().redoStack.length > 0,

        select(id) {
            set({ selectedId: id });
        },

        markSaved(version) {
            set({ savedVersion: version, saveState: "saved" });
        },

        save: () => doSave("manual"),

        resolveConflictReload() {
            const s = get();
            if (!s.conflict) return;
            set({
                document: s.conflict.serverDocument,
                savedVersion: s.conflict.currentVersion,
                conflict: null,
                saveState: "saved",
                undoStack: [],
                redoStack: [],
                externalEpoch: s.externalEpoch + 1,
            });
        },

        async resolveConflictOverwrite() {
            const s = get();
            if (!s.conflict || !s.emailId) return;
            const conflict = s.conflict;
            // Keep my document but rebase onto the server's current version.
            set({
                conflict: null,
                document: conflict.myDocument,
                savedVersion: conflict.currentVersion,
                saveState: "dirty",
            });
            await doSave("manual");
        },

        setValidation(warnings) {
            set({ validation: warnings });
        },
    };
});
